import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, TextField, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, IconButton,
    CircularProgress, Pagination, Dialog, DialogTitle, DialogContent,
    DialogActions, Card, CardContent, Autocomplete, Chip, InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../AuthProvider';
import { getApiToken } from '../authUtils';
import { useMsal } from '@azure/msal-react';
const urlconfig = require('../config/urlconfig');

// --- STYLED COMPONENTS ---
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontSize: '0.875rem',
    padding: '12px 16px',
    verticalAlign: 'middle',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&.header': {
        backgroundColor: '#f5f5f5',
        color: theme.palette.text.primary,
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        letterSpacing: '0.05em',
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
    transition: 'background-color 0.2s ease',
}));

const CatalogManager = () => {
    const { account, username } = useAuth();
    const { instance } = useMsal();

    // --- DATA STATE ---
    const [items, setItems] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);

    // --- MODAL STATE ---
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [catalogLoading, setCatalogLoading] = useState(false);

    // --- CATALOG OPTIONS ---
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categorySubcategoryMap, setCategorySubcategoryMap] = useState({});

    // --- HELPER: AUTHENTICATED REQUEST ---
    const sendAuthenticatedRequest = useCallback(async (url, method, body) => {
        try {
            const accessToken = await getApiToken(instance, account);
            const response = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
                body: body ? JSON.stringify(body) : undefined,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: response.statusText }));
                throw new Error(`API Error: ${response.status} - ${errorData.message || 'Unknown error'}`);
            }
            const contentType = response.headers.get("content-type");
            return contentType?.includes("application/json") ? response.json() : response.text();
        } catch (error) {
            console.error("Authenticated API call failed:", error);
            throw error;
        }
    }, [instance, account]);

    // --- 1. FETCH COST SHEETS ---
    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/admin/cost-sheets?search=${search}&page=${page}&size=10`, 'GET');
            if (res) {
                setItems(res.content || []);
                setTotalPages(res.totalPages || 0);
            }
        } catch(e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [page, search, sendAuthenticatedRequest]);

    useEffect(() => {
        if(account) fetchItems();
    }, [fetchItems, account]);

    // --- 2. FETCH BASE SHOP CATALOG DATA (Brands & Categories via Smart Search) ---
    const fetchCatalogData = async () => {
        if (brands.length > 0) return;
        setCatalogLoading(true);
        try {
            // Uses smart-search to pull from shop.eyantra.com
            const response = await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/smart-search/filters`, 'GET');
            if (response) {
                setBrands((response.brands || []).map(b => ({ id: b, name: b })));
                setCategories((response.categories || []).map(c => ({ id: c, name: c })));
            }
        } catch (error) {
            console.error("Failed to fetch Shop catalog data:", error);
        } finally {
            setCatalogLoading(false);
        }
    };

    // --- 3. FETCH SUBCATEGORIES DYNAMICALLY (Cascading via Smart Search) ---
    const fetchSubcategories = async (categoryName) => {
        if (!categoryName || categorySubcategoryMap[categoryName]) return;

        setCatalogLoading(true);
        try {
            const subParams = new URLSearchParams({ category: categoryName });
            const response = await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/smart-search/filters?${subParams.toString()}`, 'GET');

            // Defensive check for case differences in DTO serialization (subCategories vs subcategories)
            const subs = response?.subcategories || response?.subCategories || [];

            if (Array.isArray(subs)) {
                setCategorySubcategoryMap(prev => ({
                    ...prev,
                    [categoryName]: subs.map(s => ({ id: s, name: s }))
                }));
            }
        } catch (e) {
            console.error(`Failed to fetch subcategories for category ${categoryName}`, e);
        } finally {
            setCatalogLoading(false);
        }
    };

    // --- 4. HANDLE EDIT CLICK ---
    const handleEditClick = (item) => {
        const preparedItem = {
            ...item,
            items: item.items.map(subItem => ({
                ...subItem,
                esin: subItem.esin || '',
                ecomProductName: subItem.ecomProductName || '',
                // Empty the dropdowns so they are forced to pick from the Shop API
                brand: null,
                category: null,
                subcategory: null
            }))
        };

        setSelectedItem(preparedItem);
        setShowModal(true);
        fetchCatalogData();
    };

    // --- 5. HANDLE CHANGES & CASCADING LOGIC ---
    const handleItemChange = (index, field, value) => {
        const updatedItems = selectedItem.items.map((item, idx) => {
            if (idx === index) {
                const updatedItem = { ...item, [field]: value };
                if (field === 'category') {
                    updatedItem.subcategory = null; // Clear subcategory when category changes
                }
                return updatedItem;
            }
            return item;
        });
        setSelectedItem(prevState => ({ ...prevState, items: updatedItems }));

        // Trigger Shop Subcategory fetch using the Category Name
        if (field === 'category' && value && value.name) {
            fetchSubcategories(value.name);
        }
    };

    // --- 6. SAVE CHANGES TO OMS ---
    const handleSaveChanges = async () => {
        if (!selectedItem) return;

        try {
            const updatePromises = selectedItem.items.map(subItem => {
                return sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/admin/update-catalog-info`, 'POST', {
                    subItemId: subItem.id,
                    esin: subItem.esin,
                    ecomProductName: subItem.ecomProductName,
                    brand: subItem.brand?.name || '',
                    category: subItem.category?.name || '',
                    subcategory: subItem.subcategory?.name || '',
                    updatedBy: account.username || 'Admin'
                });
            });

            await Promise.all(updatePromises);

            alert("Catalog info updated successfully!");
            setShowModal(false);
            fetchItems();
        } catch (e) {
            console.error(e);
            alert("Error saving changes.");
        }
    };

    const isSaveDisabled = selectedItem?.items.some(item => !item.brand || !item.category || !item.subcategory);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Cost Sheet Catalog Manager
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Search cost sheets and standardize product Brand, Category, Subcategory, ESIN, and Ecom Name.
                </Typography>
            </Box>

            {/* SEARCH BAR */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                    size="small"
                    label="Search Cost Sheets (ID, Client, Product)"
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && setPage(0)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
                    }}
                    sx={{ bgcolor: 'white' }}
                />
                <Button variant="contained" onClick={() => setPage(0)} sx={{ px: 3 }}>Search</Button>
            </Box>

            {/* MAIN TABLE */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <TableContainer>
                        <Table size="medium">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell className="header">Cost Sheet ID</StyledTableCell>
                                    <StyledTableCell className="header">Client</StyledTableCell>
                                    <StyledTableCell className="header">Created By</StyledTableCell>
                                    <StyledTableCell className="header">Date</StyledTableCell>
                                    <StyledTableCell className="header" align="right">Items</StyledTableCell>
                                    <StyledTableCell className="header" align="center">Action</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => (
                                    <StyledTableRow key={item.id}>
                                        <StyledTableCell sx={{ fontWeight: 'medium', color: 'primary.main' }}>{item.salesOrderNo || 'N/A'}</StyledTableCell>
                                        <StyledTableCell>{item.clientName}</StyledTableCell>
                                        <StyledTableCell>{item.createdBy}</StyledTableCell>
                                        <StyledTableCell>{new Date(item.createdAT).toLocaleDateString()}</StyledTableCell>
                                        <StyledTableCell align="right"><Chip label={item.items?.length || 0} size="small" variant="outlined" /></StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => handleEditClick(item)} sx={{ textTransform: 'none' }}>
                                                Edit
                                            </Button>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                                {items.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>No cost sheets found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ p: 1, display: 'flex', justifyContent: 'center', bgcolor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
                        <Pagination count={totalPages} page={page + 1} onChange={(e, v) => setPage(v - 1)} color="primary" shape="rounded" />
                    </Box>
                </Paper>
            )}

            {/* EDIT MODAL */}
            <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f5f5f5', py: 2 }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Edit Catalog Info</Typography>
                        <Typography variant="caption" color="text.secondary">Cost Sheet: {selectedItem?.salesOrderNo}</Typography>
                    </Box>
                    <IconButton onClick={() => setShowModal(false)}><CloseIcon /></IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 3, bgcolor: '#ffffff' }}>
                    {selectedItem && (
                        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                <TableContainer sx={{ maxHeight: '60vh' }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell className="header" sx={{ width: '20%' }}>Product Details</StyledTableCell>
                                                <StyledTableCell className="header" sx={{ width: '15%' }}>ESIN</StyledTableCell>
                                                <StyledTableCell className="header" sx={{ width: '15%' }}>Ecom Name</StyledTableCell>
                                                <StyledTableCell className="header" sx={{ width: '16%' }}>Brand *</StyledTableCell>
                                                <StyledTableCell className="header" sx={{ width: '16%' }}>Category *</StyledTableCell>
                                                <StyledTableCell className="header" sx={{ width: '18%' }}>Subcategory *</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedItem.items.map((item, index) => (
                                                <StyledTableRow key={item.id || index}>
                                                    <StyledTableCell>
                                                        <TextField fullWidth size="small" value={item.details} disabled multiline rows={2} variant="standard" InputProps={{ disableUnderline: true }} />
                                                    </StyledTableCell>

                                                    <StyledTableCell>
                                                        <TextField fullWidth size="small" placeholder="ESIN" value={item.esin} onChange={(e) => handleItemChange(index, 'esin', e.target.value)} variant="outlined" />
                                                    </StyledTableCell>

                                                    <StyledTableCell>
                                                        <TextField fullWidth size="small" placeholder="Ecom Product Name" value={item.ecomProductName} onChange={(e) => handleItemChange(index, 'ecomProductName', e.target.value)} variant="outlined" />
                                                    </StyledTableCell>

                                                    <StyledTableCell>
                                                        <Autocomplete
                                                            options={brands}
                                                            getOptionLabel={(option) => option.name || ""}
                                                            value={item.brand}
                                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                            onChange={(event, newValue) => handleItemChange(index, 'brand', newValue)}
                                                            loading={catalogLoading}
                                                            size="small"
                                                            renderInput={(params) => <TextField {...params} label="Brand" variant="outlined" required error={!item.brand} />}
                                                        />
                                                    </StyledTableCell>

                                                    <StyledTableCell>
                                                        <Autocomplete
                                                            options={categories}
                                                            getOptionLabel={(option) => option.name || ""}
                                                            value={item.category}
                                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                            onChange={(event, newValue) => handleItemChange(index, 'category', newValue)}
                                                            loading={catalogLoading}
                                                            size="small"
                                                            renderInput={(params) => <TextField {...params} label="Category" variant="outlined" required error={!item.category} />}
                                                        />
                                                    </StyledTableCell>

                                                    <StyledTableCell>
                                                        <Autocomplete
                                                            options={item.category ? (categorySubcategoryMap[item.category.name] || []) : []}
                                                            getOptionLabel={(option) => option.name || ""}
                                                            value={item.subcategory}
                                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                            onChange={(event, newValue) => handleItemChange(index, 'subcategory', newValue)}
                                                            disabled={!item.category}
                                                            loading={catalogLoading}
                                                            size="small"
                                                            renderInput={(params) => <TextField {...params} label="Subcategory" variant="outlined" required error={!item.subcategory && !!item.category} />}
                                                        />
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="error" sx={{ visibility: isSaveDisabled ? 'visible' : 'hidden', pl: 2 }}>
                        * Please select a Brand, Category, and Subcategory for all items.
                    </Typography>
                    <Box>
                        <Button onClick={() => setShowModal(false)} variant="outlined" color="inherit" sx={{ mr: 2 }}>Cancel</Button>
                        <Button onClick={handleSaveChanges} variant="contained" color="primary" startIcon={<SaveIcon />} disabled={isSaveDisabled}>
                            Save Changes
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CatalogManager;